import { z } from 'zod';
import { GAME_IDS, type GameId } from './common';
import type { FairProof } from './fair';
import type { CarpBetContext, CarpMode } from './games/carp';
import type { DdzTier } from './games/doudizhu';
import type { FcBetContext } from './games/firecrackers';
import type { MjBetContext, MjCategory } from './games/mahjong';
import type { ZjhBetContext, ZjhCategory, ZjhResult } from './games/zhajinhua';

/**
 * Bet history (profile) and per-bet / per-round data for the provably-fair page.
 *
 *   GET /api/me/bets?game=&cursor=&limit=   → BetHistoryPage   (own bets only, newest first)
 *   GET /api/me/bets/:id                     → BetDetail        (own bet + everything needed to re-play it)
 *   GET /api/fair/round/:game/:round         → FairRoundInfo    (round = roundNo "184203" or round uuid)
 *
 * Server seeds are only present once revealed: per-user seed → after rotation; shared round → after the round ended.
 */

/** `slots` = every slot game. */
export const HISTORY_GAME_FILTERS = [...GAME_IDS, 'slots'] as const;
export type HistoryGameFilter = (typeof HISTORY_GAME_FILTERS)[number];

export const HISTORY_PAGE_MAX = 50;

export const zBetHistoryQuery = z.object({
  game: z.enum(HISTORY_GAME_FILTERS).optional(),
  /** opaque cursor from the previous page's `nextCursor` */
  cursor: z.string().min(1).max(200).optional(),
  limit: z.coerce.number().int().min(1).max(HISTORY_PAGE_MAX).default(20),
});
export type BetHistoryQuery = z.input<typeof zBetHistoryQuery>;

export type BetStatus = 'placed' | 'settled' | 'refunded';

/** Game-specific short outcome for the history row ("2.00×", "Red ×2", "2.8%", "Free spins"). */
export type BetOutcome =
  | { kind: 'crash'; /** cash-out multiplier, null = lost / not cashed yet */ cashout: number | null }
  | { kind: 'roulette'; pick: 'red' | 'black' | 'green' | null; result: 'red' | 'black' | 'green' | null }
  | { kind: 'pot'; /** share of the bank of this stake, 0..1 */ chance: number | null; won: boolean | null }
  | { kind: 'slot'; mode: 'base' | 'freeSpins' | 'holdAndWin' | 'buyBonus' | null }
  | { kind: 'carp'; mode: CarpMode; /** steps passed */ steps: number; cashedOut: boolean }
  | { kind: 'firecrackers'; mines: number; /** ingots revealed */ steps: number; cashedOut: boolean }
  | { kind: 'hongbao'; /** table stake (18 / 88 / 888) */ room: number; players: number; /** tokens in the own envelope */ envelope: number; /** own envelope was the biggest (手气最佳) */ luckiest: boolean }
  | { kind: 'mahjong'; category: MjCategory; /** exchanges made (0…3) */ exchanges: number }
  | { kind: 'mahjong-table'; /** table stake (18 / 88 / 888) */ stake: number; result: 'won' | 'lost' | 'refunded'; /** fan of the winning hand (0 = no own win) */ fan: number; /** the hand was won by a bot (stakes refunded) */ byBot: boolean; /** own seat at the table (0 = East), for the verification page */ seat?: number }
  | { kind: 'zhajinhua'; /** the player's category */ hand: ZjhCategory; /** null while the game runs */ result: ZjhResult | null }
  | {
      kind: 'doudizhu';
      tier: DdzTier;
      role: 'landlord' | 'peasant';
      /** 叫分 1…3 */
      bid: number;
      /** bid × 2^doublings, uncapped */
      multiple: number;
      /** min(multiple, capX): what was paid per pair */
      capped: number;
      /** the own side won */
      won: boolean;
      /** tokens won (+) / lost (−) before the fee */
      delta: number;
      fee: number;
      /** at least one pair was against a bot (no tokens in it) */
      vsBots: boolean;
      /** own seat (verification page) */
      seat?: number;
    }
  | {
      /** a chess game for a stake (wave C) */
      kind: 'chess';
      /** table stake (18 / 88 / 888) */
      stake: number;
      color: 'w' | 'b';
      /** playing — the game is not settled yet; aborted — no first move (stakes refunded) */
      result: 'playing' | 'won' | 'lost' | 'draw' | 'aborted';
      /** how it ended (CHESS_END_REASONS), null while playing */
      reason: string | null;
      /** half-moves played */
      plies: number;
    }
  | {
      /** a skill puzzle's prize-pool competition entry (the bet is the entry fee) */
      kind: 'contest';
      period: 'hour' | 'day' | null;
      /** 1-based place among the solvers once settled; null — running, unsolved or refunded */
      rank: number | null;
      entrants: number | null;
      /** the server confirmed the solution; null before the settlement */
      solved: boolean | null;
    }
  | { kind: 'other' };

export interface BetHistoryItem {
  id: string;
  game: GameId;
  amount: number;
  payout: number;
  multiplier: number | null;
  status: BetStatus;
  createdAt: string;
  /** shared rounds */
  roundId: string | null;
  /** public round number ("#184203") */
  roundNo: number | null;
  /** per-user seed games (slots): nonce of the bet */
  nonce: number | null;
  outcome: BetOutcome;
  /** null for bets without fairness data */
  fair: FairProof | null;
}

export interface BetHistoryPage {
  items: BetHistoryItem[];
  nextCursor: string | null;
}

/** Slot spin as stored with the bet: enough to re-play it (engine `verifySpin`). */
export interface SlotBetContext {
  bet: number;
  action: string;
  stateBefore: unknown;
  /** stored SpinResult (compared with the re-computed one) */
  result: unknown;
  payout: number;
  /** strips / pays version of a retuned slot (stored with the spin); absent / null = 1, the original table */
  table?: number | null;
}

export interface BetDetail extends BetHistoryItem {
  /** shared-round games: round data (result only after the round ended) */
  round: FairRoundInfo | null;
  /** slot games */
  slot: SlotBetContext | null;
  /** carp: mode, picks and the rock layout (layout only once the game is over) */
  carp: CarpBetContext | null;
  /** firecrackers: mines, revealed cells and the firecracker layout (layout only once the game is over) */
  firecrackers: FcBetContext | null;
  /** mahjong: first hand, discards per exchange, final hand; the deck only once the seed is rotated */
  mahjong: MjBetContext | null;
  /** golden flower: stakes, decision, the player's and (once over) the dealer's cards */
  zhajinhua: ZjhBetContext | null;
  /** puzzle contest entry: the entrant's copy of the window's task (the transformation once the seed is revealed) */
  contest: ContestBetContext | null;
}

/**
 * A puzzle contest entry (docs/superpowers/skill-platform.md «Копия задачи»): every entrant plays an isomorphic copy of
 * the window's task. The /fair page re-generates the task from the revealed round seed, re-derives the transformation
 * from the seed and `player` (@tg/games `<game>ContestChallenge`) and compares both with what is stored here.
 */
export interface ContestBetContext {
  /** the key of the copy: the entrant's user id (the transformation stream is HMAC(seed, "variant:<game>:<player>")) */
  player: string;
  /** daily-words: the language of the task */
  locale: string;
  /** the task the entrant played, as stored at the entry */
  challenge: unknown;
  /** the stored transformation — null until the seed is revealed, or for an entry made before per-entrant copies */
  variant: unknown;
}

export type RoundStatusPublic = 'open' | 'running' | 'finished' | 'cancelled';

export interface FairRoundInfo extends FairProof {
  game: GameId;
  roundId: string;
  roundNo: number;
  status: RoundStatusPublic;
  /** public result stored at the end of the round (crashCents / cell / entries + winnerIndex …); null until then */
  result: unknown;
  startedAt: string;
  endedAt: string | null;
}
